import { useState } from 'react'
import { formatMoney, toNumber } from '@shop/shared'
import { Banner, ErrorBanner, Field, Select } from '@shop/ui'
import type { Warehouse } from '@shop/api-client'
import { Keypad, TouchButton } from '../pos/touch'
import { openShift } from '../pos/api'

/**
 * فتحُ الورديّة — <b>أوّلُ شاشةٍ في اليوم، وضغطةٌ واحدة</b>.
 *
 * ⚠ <b>ولا بيعَ يُتمّ بلا ورديّةٍ مفتوحة</b> — قرارُ المالك، ولو كان البيعُ
 *   بالبطاقة وحدَها. والخادمُ يرفض بـ<c>sales.shift_required</c>؛ وهذه الشاشةُ
 *   تمنع الوصولَ إلى ذلك الرفض أصلًا.
 *
 * <b>والنقدُ الافتتاحيُّ ما عدّه الكاشيرُ في الدرج</b> — لا رقمٌ يُفترض:
 * صفرٌ مفترضٌ يجعل فرقَ آخر اليوم بلا تفسير.
 */
export default function OpenShift({
  branchId,
  warehouses,
  warehouseId,
  onChooseWarehouse,
  onOpened,
}: {
  branchId: string
  warehouses: Warehouse[]
  warehouseId: string | null
  onChooseWarehouse: (id: string) => void
  onOpened: () => void
}) {
  const [cash, setCash] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const amount = toNumber(cash) ?? 0

  const submit = async () => {
    setBusy(true)
    setError(null)

    try {
      await openShift(branchId, amount)
      onOpened()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto grid min-h-dvh max-w-md content-start gap-4 p-4">
      <h1 className="pt-4 text-center text-2xl font-bold">افتح ورديّةَ الصندوق</h1>

      <Banner tone="info">
        ولا يُتمّ بيعٌ بلا ورديّةٍ مفتوحة — <b>ولو كان بالبطاقة وحدَها</b>.
      </Banner>

      {warehouses.length > 1 && (
        <Field label="المستودعُ الذي تبيع منه" hint="يُحسم مرّةً في أوّل اليوم.">
          <Select value={warehouseId ?? ''} onChange={onChooseWarehouse}>
            <option value="">اختر…</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.nameAr}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <div className="rounded-xl border border-line bg-surface-raised p-4">
        <div className="text-center text-xs text-ink-soft">النقدُ في الدرج الآن</div>
        <div className="tabular mt-1 text-center text-3xl font-bold" dir="ltr">
          {formatMoney(amount)}
        </div>
      </div>

      <Keypad value={cash} onChange={setCash} />

      <ErrorBanner error={error} />

      <TouchButton
        tone="primary"
        wide
        disabled={busy || !warehouseId}
        onClick={() => void submit()}
      >
        {busy ? 'يُفتح…' : 'افتح الورديّة'}
      </TouchButton>

      {!warehouseId && warehouses.length > 1 && (
        <p className="text-center text-xs text-ink-soft">اختر المستودعَ أوّلًا.</p>
      )}
    </div>
  )
}
